<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getContrastingTextColor } from '@chatwoot/utils';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  handler: {
    type: String,
    default: 'ai',
    validator: value => ['ai', 'team'].includes(value),
  },
  theme: {
    type: String,
    default: 'bright',
    validator: value => ['bright', 'dark'].includes(value),
  },
  accentColor: { type: String, default: '#6e56cf' },
});

const { t } = useI18n();

const isDark = computed(() => props.theme === 'dark');
const isTeam = computed(() => props.handler === 'team');

// Accent color is user-picked, so dynamic style bindings are required here.
const headerStyles = computed(() => ({
  background: `linear-gradient(135deg, ${props.accentColor} 0%, color-mix(in srgb, ${props.accentColor} 84%, #16102e) 100%)`,
  color: getContrastingTextColor(props.accentColor),
}));

const accentStyles = computed(() => ({
  backgroundColor: props.accentColor,
  color: getContrastingTextColor(props.accentColor),
}));

const agentName = computed(() =>
  isTeam.value
    ? t('ONBOARDING_V2.STAGE.WIDGET.TEAM_NAME')
    : t('ONBOARDING_V2.STAGE.WIDGET.AI_NAME')
);

const agentSubtitle = computed(() =>
  isTeam.value
    ? t('ONBOARDING_V2.STAGE.WIDGET.TEAM_SUBTITLE')
    : t('ONBOARDING_V2.STAGE.WIDGET.AI_SUBTITLE')
);

const teamInitial = computed(() => agentName.value.charAt(0));
</script>

<template>
  <div
    class="w-[19.5rem] overflow-hidden rounded-[1.25rem] shadow-[0_30px_70px_-28px_rgba(30,20,70,0.55)] transition-colors duration-500"
    :class="isDark ? 'bg-[#1c1a24] text-[#f4f2fa]' : 'bg-white text-[#1b1926]'"
  >
    <!-- Branded header band -->
    <header
      class="flex items-center gap-3 px-4 pb-5 pt-3.5 transition-colors duration-300"
      :style="headerStyles"
    >
      <span
        class="relative grid h-[1.8125rem] w-[1.8125rem] flex-shrink-0 place-items-center rounded-full text-xs font-bold text-white ring-2 ring-white/30"
        :class="
          isTeam
            ? 'bg-[#d0644a]'
            : 'bg-[linear-gradient(135deg,#8b74ff,#c94f9b)]'
        "
      >
        <Icon v-if="!isTeam" icon="i-ph-sparkle-fill" class="size-3" />
        <template v-else>{{ teamInitial }}</template>
        <!-- Presence dot -->
        <span
          class="absolute -right-px -top-px h-[0.4375rem] w-[0.4375rem] rounded-full border-2 bg-[#3fca7d]"
          :style="{ borderColor: accentColor }"
        />
      </span>
      <span class="min-w-0">
        <span class="block truncate text-[0.8125rem] font-bold">
          {{ agentName }}
        </span>
        <span class="block truncate text-[0.65625rem] opacity-80">
          {{ agentSubtitle }}
        </span>
      </span>
    </header>

    <!-- Sample conversation -->
    <div class="px-4 pb-1 pt-3">
      <div
        class="mb-2 max-w-[85%] rounded-[0.25rem_0.8125rem_0.8125rem_0.8125rem] px-3 py-2 text-[0.78125rem] leading-normal transition-colors duration-500"
        :class="isDark ? 'bg-[#292634]' : 'bg-[#f4f3f8]'"
      >
        {{ t('ONBOARDING_V2.STAGE.WIDGET.AGENT_MESSAGE') }}
      </div>
      <div
        class="mb-2 ms-auto w-fit max-w-[85%] rounded-[0.8125rem_0.25rem_0.8125rem_0.8125rem] px-3 py-2 text-[0.78125rem] leading-normal transition-colors duration-300"
        :style="accentStyles"
      >
        {{ t('ONBOARDING_V2.STAGE.WIDGET.USER_MESSAGE') }}
      </div>
    </div>

    <!-- Input pill -->
    <div
      class="mx-4 mb-4 mt-1 flex items-center gap-2 rounded-full px-3.5 py-2 text-[0.78125rem] transition-colors duration-500"
      :class="
        isDark ? 'bg-[#292634] text-[#a09db0]' : 'bg-[#f4f3f8] text-[#6f6c7a]'
      "
    >
      {{ t('ONBOARDING_V2.STAGE.WIDGET.INPUT_PLACEHOLDER') }}
      <span
        class="ms-auto grid h-[1.375rem] w-[1.375rem] flex-shrink-0 place-items-center rounded-full transition-colors duration-300"
        :style="accentStyles"
      >
        <Icon icon="i-ph-arrow-up-bold" class="size-3" />
      </span>
    </div>
  </div>
</template>
